import { useState, useEffect, useCallback } from 'react';

function isStandaloneMode() {
  try {
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  } catch { return false; }
}

function isIOSSafari() {
  const ua = window.navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return isIOS && isSafari;
}

// Real bug, reported live (2026-08-29): this is a fixed-position button on
// every page, and used to be allowed to render the instant the browser
// fired beforeinstallprompt (or immediately, for iOS Safari) — often before
// a mobile visitor had even finished reading the hero, then stayed pinned
// over the pricing section for the rest of the session. Holding it back a
// few seconds after mount means it isn't the first thing competing for
// attention, without touching its position/design.
const SHOW_DELAY_MS = 8000;

// SOLO SU UN TELEFONO VERO (2026-09-12).
//
// La larghezza da sola non basta: una finestra stretta su un Mac non e' un
// telefono, e l'invito a "installare l'app" li' non ha senso. La coppia che
// distingue davvero e' il tipo di puntatore — `pointer: coarse` vuol dire
// dito, non mouse — insieme a una larghezza da telefono.
// Si legge a ogni cambio, non una volta sola: si ruota il telefono, si
// collega un mouse a un tablet, si trascina una finestra su un altro schermo.
function isTelefono() {
  try {
    return window.matchMedia('(pointer: coarse) and (max-width: 900px)').matches;
  } catch { return false; }
}

export default function useInstallPrompt() {
  const [standaloneAtLoad] = useState(isStandaloneMode);
  const [telefono, setTelefono] = useState(isTelefono);

  useEffect(() => {
    let mq;
    try { mq = window.matchMedia('(pointer: coarse) and (max-width: 900px)'); } catch { return undefined; }
    const aggiorna = () => setTelefono(mq.matches);
    mq.addEventListener('change', aggiorna);
    window.addEventListener('resize', aggiorna);
    return () => { mq.removeEventListener('change', aggiorna); window.removeEventListener('resize', aggiorna); };
  }, []);
  const [ios] = useState(isIOSSafari);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [readyToShow, setReadyToShow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setReadyToShow(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (standaloneAtLoad) return;
    function onBeforeInstall(e) {
      e.preventDefault();
      setDeferredPrompt(e);
    }
    function onInstalled() {
      setInstalled(true);
      setDeferredPrompt(null);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [standaloneAtLoad]);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try { await deferredPrompt.userChoice; } catch {}
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  return {
    standalone: standaloneAtLoad || installed,
    // Fuori da un telefono i due interruttori restano spenti: il componente
    // non disegna niente, e non essendoci niente non riserva nemmeno spazio.
    ios: telefono && readyToShow && ios,
    canInstall: telefono && readyToShow && !!deferredPrompt,
    promptInstall,
  };
}
